<template>
  <div class="navbar-menu-container h-full">
    <div
      class="navbar-menu-header brand-gradient-h pb-3 pt-[max(0.75rem,var(--ion-safe-area-top,0.75rem))]"
    >
      <div class="row-center-gap-1 ms-3">
        <CustomImage
          class="navbar-icon w-auto h-6 l:h-7"
          :src="DashboardLogoWhite"
          :alt="AppName"
        />
      </div>
    </div>
    <nav
      v-if="AppMenu"
      class="navbar-menu flex flex-col justify-between flex-1 h-full bg-color-light-translucent"
      aria-label="Main navigation"
    >
      <ul class="navbar-menu-list flex-1 list-none p-0 m-0">
        <li
          v-for="(menuItem, menuIndex) in AppMenu.MenuItems"
          :key="menuItem.MenuName || menuIndex"
          class="navbar-menu-list-item m-0"
        >
          <NavbarMenuItem
            v-if="!menuItem.ChildItems || menuItem.ChildItems.length === 0"
            variant="leaf"
            :to="menuItem.ViewRoute || ''"
            :icon="menuItem.MenuIcon || ''"
            :label="menuItem.DisplayTitle"
            @navigate="onMenuItemClicked"
          />
          <template v-else>
            <NavbarMenuItem
              variant="parent"
              :icon="menuItem.MenuIcon || ''"
              :label="menuItem.DisplayTitle"
              :expanded="activeSubMenuIndex === menuIndex"
              :panel-id="getPanelId(menuIndex)"
              @toggle="onParentClick(menuIndex)"
            />
            <ul
              :id="getPanelId(menuIndex)"
              v-show="activeSubMenuIndex === menuIndex"
              class="navbar-menu-children list-none m-0 p-0"
              role="region"
              :aria-label="`${menuItem.DisplayTitle} sub-menu`"
            >
              <li
                v-for="(childItem, childIndex) in menuItem.ChildItems"
                :key="childItem.MenuName || childIndex"
                class="navbar-menu-list-item m-0"
              >
                <NavbarMenuItem
                  v-if="childItem.ViewRoute"
                  variant="child"
                  :to="childItem.ViewRoute"
                  :icon="childItem.MenuIcon || menuItem.MenuIcon || ''"
                  :label="childItem.DisplayTitle"
                  @navigate="onMenuItemClicked"
                />
              </li>
            </ul>
          </template>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script lang="ts">
import NavbarMenuItem from "@/components/layout/NavbarMenuItem.vue";
import CustomImage from "@/components/primitives/CustomImage.vue";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { menuController } from "@ionic/vue";
import { storeToRefs } from "pinia";
import { defineComponent, ref, watch } from "vue";

export default defineComponent({
  name: "NavbarMenu",
  components: { CustomImage, NavbarMenuItem },
  setup() {
    const appSettingsStore = useAppSettingsStore();
    const { AppName, AppMenu, DashboardLogoWhite } = storeToRefs(appSettingsStore);
    // Keyed by position, not by MenuName: the menu config can hand us a group with a null/blank
    // MenuName, and a name-based key would then match the "nothing expanded" sentinel and render
    // that group open on first paint. An index can never collide with the sentinel.
    const activeSubMenuIndex = ref<number | null>(null);

    // This component sits in App.vue's CustomIonMenu, OUTSIDE <IonRouterOutlet>, so logging out
    // never unmounts it — resetRouterOutlet() only remounts the outlet. Without this, the group
    // left open on the way out (Profile, on the way to Logout) is still open for whoever logs in
    // next, and only a hard reload clears it. resetMenus() nulls AppMenu on both logout and login,
    // so watching it collapses the menu exactly when the session it belonged to ends.
    watch(AppMenu, () => {
      activeSubMenuIndex.value = null;
    });

    function getPanelId(menuIndex: number) {
      return `navbar-menu-panel-${menuIndex}`;
    }

    async function onMenuItemClicked(): Promise<void> {
      await menuController.close("navbar-menu");
    }

    function onParentClick(menuIndex: number) {
      activeSubMenuIndex.value = activeSubMenuIndex.value === menuIndex ? null : menuIndex;
    }

    return {
      AppName,
      AppMenu,
      DashboardLogoWhite,
      activeSubMenuIndex,
      getPanelId,
      onMenuItemClicked,
      onParentClick,
    };
  },
});
</script>
